import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  ArrowUpFromLine,
  Check,
  Loader2,
  RefreshCw,
  Shield,
  ShieldAlert,
  TrendingDown,
  Activity,
  X as XIcon,
} from 'lucide-react';
import {
  FailurePatternReport,
  FixtureCandidate,
  TelemetryStatus,
  getFailurePatterns,
  getTelemetryStatus,
  listFixtureCandidates,
  promoteFixtureCandidate,
  rejectFixtureCandidate,
} from '../services/smartStartApi';
import { useAuth } from '../contexts/AuthContext';
import { useSmartStartModal } from '../components/SmartStart/Modal';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

const VerificationFeedbackAdmin: React.FC = () => {
  const { user } = useAuth();
  const { modal, confirm: modalConfirm } = useSmartStartModal();

  const [candidates, setCandidates] = useState<FixtureCandidate[]>([]);
  const [patterns, setPatterns] = useState<FailurePatternReport | null>(null);
  const [telemetry, setTelemetry] = useState<TelemetryStatus | null>(null);

  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [loadingTelemetry, setLoadingTelemetry] = useState(false);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);

  const [patternWindowDays, setPatternWindowDays] = useState(14);
  const [patternMinOccurrences, setPatternMinOccurrences] = useState(3);
  const [patternModuleFilter, setPatternModuleFilter] = useState('');
  const [patternClaimTypeFilter, setPatternClaimTypeFilter] = useState('');

  const isAuthorised = useMemo(
    () => user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER',
    [user]
  );
  const canPromote = useMemo(
    () => user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER',
    [user]
  );

  const loadCandidates = useCallback(async () => {
    try {
      setLoadingCandidates(true);
      const list = await listFixtureCandidates();
      setCandidates(list);
    } catch (err: any) {
      toast.error(`Failed to load candidates: ${err?.response?.data?.error || err.message}`);
    } finally {
      setLoadingCandidates(false);
    }
  }, []);

  const loadPatterns = useCallback(async () => {
    try {
      setLoadingPatterns(true);
      const report = await getFailurePatterns({
        windowDays: patternWindowDays,
        minOccurrences: patternMinOccurrences,
        module: patternModuleFilter || undefined,
      });
      setPatterns(report);
    } catch (err: any) {
      toast.error(`Failed to detect patterns: ${err?.response?.data?.error || err.message}`);
    } finally {
      setLoadingPatterns(false);
    }
  }, [patternWindowDays, patternMinOccurrences, patternModuleFilter]);

  const loadTelemetry = useCallback(async () => {
    try {
      setLoadingTelemetry(true);
      const status = await getTelemetryStatus();
      setTelemetry(status);
    } catch (err: any) {
      toast.error(`Failed to read telemetry status: ${err?.response?.data?.error || err.message}`);
    } finally {
      setLoadingTelemetry(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthorised) return;
    loadCandidates();
    loadPatterns();
    loadTelemetry();
  }, [isAuthorised, loadCandidates, loadPatterns, loadTelemetry]);

  const handleReject = async (candidate: FixtureCandidate) => {
    const confirmed = await modalConfirm({
      title: 'Reject this candidate?',
      description: `"${candidate.candidate_id}" will be marked rejected and removed from the queue. Provide an optional reason for the audit trail.`,
      variant: 'danger',
      confirmLabel: 'Reject',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
    try {
      setRejecting(candidate.candidate_id);
      await rejectFixtureCandidate(candidate.candidate_id);
      toast.success('Candidate rejected');
      await loadCandidates();
    } catch (err: any) {
      toast.error(`Reject failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setRejecting(null);
    }
  };

  const handlePromote = async (candidate: FixtureCandidate) => {
    const confirmed = await modalConfirm({
      title: 'Promote candidate into the golden test suite?',
      description: `This moves "${candidate.candidate_id}" into backend/tests/accuracy/cases/${candidate.module}/. It becomes part of the CI regression suite. Make sure you've reviewed the JSON payload below.`,
      variant: 'info',
      confirmLabel: 'Promote',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) return;
    try {
      setPromoting(candidate.candidate_id);
      const { destination } = await promoteFixtureCandidate(candidate.candidate_id);
      toast.success(`Promoted to ${destination}`);
      await loadCandidates();
    } catch (err: any) {
      toast.error(`Promote failed: ${err?.response?.data?.error || err.message}`);
    } finally {
      setPromoting(null);
    }
  };

  if (!isAuthorised) {
    return (
      <div className="p-6">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-600" />
          <span className="text-sm text-amber-800">
            Admin role required to view verification feedback data.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {modal}

      <div className="flex items-start gap-3">
        <div className="p-2.5 bg-lavender/10 rounded-xl">
          <Shield className="w-5 h-5 text-lavender" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Verification Feedback</h1>
            <AdminSecurityHelpButton screenId="verification-feedback" size="sm" />
          </div>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Production signal feeding back into the golden test suite, calibration, and prompt
            engineering. Three feedback loops:{' '}
            <strong>E-4</strong> fixture candidates,{' '}
            <strong>E-5</strong> calibration, and{' '}
            <strong>E-6</strong> failure patterns.
          </p>
        </div>
      </div>

      {/* Telemetry status */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-lavender" />
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
              Telemetry status (E-3)
            </h2>
          </div>
          <button
            onClick={loadTelemetry}
            disabled={loadingTelemetry}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 disabled:opacity-50"
          >
            {loadingTelemetry ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
        {telemetry ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className={`p-2 rounded-lg ${telemetry.enabled ? 'bg-emerald-50' : 'bg-gray-50'}`}>
              <div className="text-gray-500">Enabled</div>
              <div className="font-bold text-gray-900">{telemetry.enabled ? 'Yes' : 'No'}</div>
            </div>
            <div className={`p-2 rounded-lg ${telemetry.sdk_active ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <div className="text-gray-500">SDK Active</div>
              <div className="font-bold text-gray-900">{telemetry.sdk_active ? 'Yes' : 'No'}</div>
            </div>
            <div className="p-2 rounded-lg bg-gray-50 col-span-2">
              <div className="text-gray-500">Endpoint</div>
              <div className="font-mono text-[11px] text-gray-800 truncate">
                {telemetry.endpoint || '(not configured)'}
              </div>
            </div>
            {telemetry.sdk_error && (
              <div className="p-2 rounded-lg bg-red-50 col-span-2 md:col-span-4">
                <div className="text-red-700 font-semibold">SDK error</div>
                <div className="text-[11px] text-red-800">{telemetry.sdk_error}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-500">Loading…</div>
        )}
      </section>

      {/* Fixture candidates */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ArrowUpFromLine className="w-4 h-4 text-lavender" />
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
              Fixture candidates (E-4)
            </h2>
            <span className="text-xs text-gray-500">{candidates.length} pending</span>
          </div>
          <button
            onClick={loadCandidates}
            disabled={loadingCandidates}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 disabled:opacity-50"
          >
            {loadingCandidates ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Captured when two verification streams flagged the same claim independently. Promoting adds
          the candidate to <code className="bg-gray-100 px-1 rounded text-[10px]">backend/tests/accuracy/cases/</code> as a
          new golden fixture.
        </p>

        {candidates.length === 0 ? (
          <div className="text-xs text-gray-500 italic py-4 text-center">
            No candidates pending. New candidates are captured automatically when production
            verification runs flag hallucinations in two independent streams.
          </div>
        ) : (
          <div className="space-y-2">
            {candidates.map((c) => (
              <div key={c.candidate_id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-bold text-gray-900">{c.module}</span>
                      <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                        c.provenance.severity === 'high' ? 'bg-red-100 text-red-700'
                        : c.provenance.severity === 'medium' ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}>
                        {c.provenance.severity}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        captured {new Date(c.captured_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-800 mb-1">{c.fixture.description}</div>
                    <div className="text-[11px] text-gray-500">
                      Verifier sources: {c.provenance.verifier_sources.join(', ')}
                    </div>
                  </div>
                  {canPromote && (
                    <div className="flex-shrink-0 flex items-center gap-1.5">
                      <button
                        onClick={() => handlePromote(c)}
                        disabled={promoting === c.candidate_id || rejecting === c.candidate_id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-lavender text-white rounded-lg hover:bg-lavender-deep disabled:opacity-50"
                      >
                        {promoting === c.candidate_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                        Promote
                      </button>
                      <button
                        onClick={() => handleReject(c)}
                        disabled={promoting === c.candidate_id || rejecting === c.candidate_id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                      >
                        {rejecting === c.candidate_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <XIcon className="w-3 h-3" />
                        )}
                        Reject
                      </button>
                    </div>
                  )}
                </div>
                {c.fixture.forbidden_claims.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-gray-600 cursor-pointer hover:text-gray-900">
                      Forbidden claims ({c.fixture.forbidden_claims.length})
                    </summary>
                    <div className="mt-1 space-y-1 pl-3">
                      {c.fixture.forbidden_claims.map((f, i) => (
                        <div key={i} className="text-[11px] text-gray-700">
                          <span className="font-mono bg-white px-1 rounded">{f.phrase}</span>
                          {f.reason && <span className="text-gray-500"> — {f.reason}</span>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Failure patterns */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-lavender" />
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
              Failure patterns (E-6)
            </h2>
          </div>
          <button
            onClick={loadPatterns}
            disabled={loadingPatterns}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 disabled:opacity-50"
          >
            {loadingPatterns ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
              Window (days)
            </label>
            <input
              type="number"
              min={1}
              max={90}
              value={patternWindowDays}
              onChange={(e) => setPatternWindowDays(parseInt(e.target.value, 10) || 14)}
              className="w-full mt-1 p-1.5 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
              Min occurrences
            </label>
            <input
              type="number"
              min={2}
              max={50}
              value={patternMinOccurrences}
              onChange={(e) => setPatternMinOccurrences(parseInt(e.target.value, 10) || 3)}
              className="w-full mt-1 p-1.5 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
              Module filter (optional)
            </label>
            <input
              type="text"
              value={patternModuleFilter}
              onChange={(e) => setPatternModuleFilter(e.target.value)}
              placeholder="e.g. rcsa_analyzer"
              className="w-full mt-1 p-1.5 text-xs border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
              Claim type / category
            </label>
            <input
              type="text"
              value={patternClaimTypeFilter}
              onChange={(e) => setPatternClaimTypeFilter(e.target.value)}
              placeholder="e.g. hallucination"
              className="w-full mt-1 p-1.5 text-xs border border-gray-300 rounded"
            />
          </div>
        </div>

        {!patterns ? (
          <div className="text-xs text-gray-500">Loading…</div>
        ) : (
          <>
            <div className="text-xs text-gray-600 mb-3">
              {patterns.total_traces} trace(s) over {patterns.window_days} day
              {patterns.window_days === 1 ? '' : 's'}. Report generated{' '}
              {new Date(patterns.generated_at).toLocaleString()}.
            </div>

            {patterns.module_stats.length > 0 && (
              <div className="mb-4">
                <h3 className="text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-2">
                  Module stats
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100 text-[10px] uppercase text-gray-600">
                        <th className="px-2 py-1 text-left">Module</th>
                        <th className="px-2 py-1 text-right">Runs</th>
                        <th className="px-2 py-1 text-right">Low-confidence</th>
                        <th className="px-2 py-1 text-right">Low-conf %</th>
                        <th className="px-2 py-1 text-right">Avg confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patterns.module_stats.map((m) => (
                        <tr key={m.module} className="border-t border-gray-100">
                          <td className="px-2 py-1 font-medium text-gray-800">{m.module}</td>
                          <td className="px-2 py-1 text-right">{m.runs}</td>
                          <td className="px-2 py-1 text-right">{m.low_confidence_runs}</td>
                          <td className={`px-2 py-1 text-right font-semibold ${
                            m.low_confidence_rate_pct >= 40 ? 'text-red-700'
                            : m.low_confidence_rate_pct >= 20 ? 'text-amber-700'
                            : 'text-emerald-700'
                          }`}>
                            {m.low_confidence_rate_pct}%
                          </td>
                          <td className="px-2 py-1 text-right">{m.avg_confidence}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {patterns.recommendations.length > 0 && (
              <div className="mb-4">
                <h3 className="text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-2">
                  Top recommendations
                </h3>
                <div className="space-y-1">
                  {patterns.recommendations.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px]">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span className="text-amber-900">{r.suggestion}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {patterns.patterns.length > 0 && (
              <details>
                <summary className="text-[11px] font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:text-gray-900">
                  Raw pattern list ({patterns.patterns.filter(p => !patternClaimTypeFilter || p.category?.toLowerCase().includes(patternClaimTypeFilter.toLowerCase())).length})
                </summary>
                <div className="mt-2 space-y-1">
                  {patterns.patterns.filter(p => !patternClaimTypeFilter || p.category?.toLowerCase().includes(patternClaimTypeFilter.toLowerCase())).map((p, i) => (
                    <div key={i} className="p-2 bg-gray-50 border border-gray-200 rounded text-[11px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900">{p.module}</span>
                        <span className="text-gray-500">/ {p.category}</span>
                        <span className="ml-auto font-semibold text-red-700">
                          {p.occurrences}×
                        </span>
                      </div>
                      {p.sample_claims.length > 0 && (
                        <ul className="list-disc list-inside text-gray-700 space-y-0.5">
                          {p.sample_claims.map((s, j) => (
                            <li key={j} className="truncate">{s}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default VerificationFeedbackAdmin;
